import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowRight, Instagram } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_PHOTOS = [
  '/images/IMG_2538.jpg',
  '/images/IMG_4541.JPG',
  '/images/IMG_6359.jpg',
  '/images/IMG_6142.jpg',
  '/images/IMG_4074.JPG',
  '/images/IMG_1708.jpg',
];

const Home = () => {
  const [latestTravels, setLatestTravels] = useState([]);
  const [currentPhoto, setCurrentPhoto] = useState(0);

  useEffect(() => {
    const fetchTravels = async () => {
      try {
        const response = await axios.get(`${API}/travels`);
        setLatestTravels(response.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching travels:', error);
      }
    };
    fetchTravels();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPhoto(prev => (prev + 1) % HERO_PHOTOS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div>
      <Helmet>
        <title>Chichi in Viaggio | Viaggi Lenti in Famiglia con Bambini</title>
        <meta name="description" content="Blog di viaggi in famiglia con bambini piccoli. Esperienze reali, itinerari testati e consigli pratici per viaggiare lentamente e in modo autentico." />
        <meta property="og:title" content="Chichi in Viaggio | Viaggi Lenti in Famiglia con Bambini" />
        <meta property="og:description" content="Esperienze reali di famiglia in viaggio con bambini. Consigli pratici per viaggiare lentamente." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://chichiinviaggio.it" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          {HERO_PHOTOS.map((src, i) => (
            <img
              key={src}
              src={src}
              alt="Famiglia in viaggio"
              loading={i === 0 ? 'eager' : 'lazy'}
              className="absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-1000"
              style={{ opacity: i === currentPhoto ? 1 : 0 }}
            />
          ))}
          <div className="absolute inset-0 bg-foreground/45 z-10" />
        </div>

        <div className="relative z-10 text-center px-6 md:px-12 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="mb-6"
          >
            <span className="inline-flex items-center text-white/90 text-sm tracking-widest font-light">
              SLOW FAMILY TRAVEL
            </span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: 'easeOut' }}
            className="font-heading font-light text-5xl md:text-7xl tracking-tight leading-tight mb-6 text-white"
            data-testid="hero-title"
          >
            Viaggiare è sentirsi a casa, ovunque.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
            className="text-lg md:text-xl leading-relaxed text-white/90 mb-10 max-w-3xl mx-auto"
            data-testid="hero-subtitle"
          >
            Esperienze reali, consigli pratici e ispirazione per viaggiare con bambini piccoli.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link
              to="/viaggi"
              className="inline-flex items-center gap-2 px-8 py-4 text-base font-medium bg-white text-foreground hover:bg-white/90 transition-all duration-300"
              data-testid="hero-cta-button"
            >
              Scopri i viaggi
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/collaborazioni"
              className="inline-flex items-center gap-2 px-8 py-4 text-base font-medium border-2 border-white text-white hover:bg-white hover:text-foreground transition-all duration-300"
              data-testid="hero-cta-collab"
            >
              Collaboriamo
            </Link>
          </motion.div>
        </div>

        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1 cursor-pointer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          data-testid="scroll-indicator"
        >
          <span className="text-white/70 text-xs tracking-widest uppercase">Scopri</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M5 12l7 7 7-7"/>
            </svg>
          </motion.div>
        </motion.div>

        <div className="absolute bottom-8 right-8 z-10 flex gap-2" data-testid="hero-dots">
          {HERO_PHOTOS.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPhoto(i)}
              className={`rounded-full transition-all duration-300 ${
                i === currentPhoto ? 'w-6 h-2 bg-white' : 'w-2 h-2 bg-white/40 hover:bg-white/70'
              }`}
              aria-label={`Foto ${i + 1}`}
            />
          ))}
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-background to-muted" data-testid="manifesto-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading font-light text-5xl md:text-7xl tracking-tight mb-4 text-foreground">
              Il nostro progetto
            </h2>
            <div className="w-20 h-1 bg-primary mx-auto rounded-full" />
          </motion.div>

          <div className="space-y-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="flex gap-6 items-start"
            >
              <div className="hidden md:block w-[3px] bg-primary/40 self-stretch rounded-full flex-shrink-0" />
              <p className="font-serif text-xl md:text-2xl leading-relaxed text-foreground/90">
                Per noi <strong>viaggiare non è collezionare mete</strong>, ma <strong>coltivare consapevolezza</strong>. Non è spostarsi da un punto all'altro, ma attraversare esperienze che lasciano tracce profonde.
              </p>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="text-lg leading-relaxed text-foreground/70 md:pl-9"
            >
              È imparare a meravigliarsi, ad adattarsi, ad ascoltare. È crescere insieme, come famiglia, trasformando ogni partenza in un'occasione di scoperta e ogni ritorno in un nuovo inizio.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="border-l-4 border-primary/60 pl-8 py-4"
            >
              <p className="text-lg leading-relaxed text-foreground/80">
                Attraverso questo spazio desideriamo trasmettere che il viaggio più importante non è quello che ci porta lontano, ma quello che ci cambia dentro.
              </p>
              <p className="text-lg leading-relaxed text-foreground/80 mt-4">
                Ogni passo nel mondo può diventare un seme di <strong>consapevolezza, empatia e gratitudine</strong>.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {latestTravels.length > 0 && (
        <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-muted" data-testid="latest-travels-section">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading font-light text-5xl md:text-7xl tracking-tight mb-12" data-testid="latest-travels-title">
              Ultimi viaggi
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {latestTravels.map((travel, index) => (
                <motion.div
                  key={travel.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                  data-testid={`travel-card-${index}`}
                >
                  <Link
                    to={`/viaggi/${travel.slug}`}
                    className="group block bg-card rounded-2xl overflow-hidden shadow-[0_4px_20px_rgb(0,0,0,0.06)] hover:shadow-[0_12px_40px_rgb(0,0,0,0.12)] transition-all duration-500"
                  >
                    <div className="aspect-[4/3] overflow-hidden relative">
                      <img
                        src={travel.cover_image}
                        alt={travel.titolo}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </div>
                    <div className="p-6">
                      <h3 className="font-serif text-2xl font-medium mb-2 group-hover:text-accent transition-colors duration-300">
                        {travel.titolo}
                      </h3>
                      <p className="text-foreground/60 mb-4 flex items-center gap-2">
                        <span className="w-1 h-1 rounded-full bg-accent" />
                        {travel.destinazione}
                      </p>
                      <p className="text-foreground/80 leading-relaxed line-clamp-3">
                        {travel.payoff}
                      </p>
                      <div className="mt-4 inline-flex items-center gap-2 text-accent font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        Scopri di più <ArrowRight size={16} />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-12">
              <Link
                to="/viaggi"
                className="inline-flex items-center gap-2 text-lg font-medium text-accent hover:text-accent/80 transition-colors"
                data-testid="view-all-travels-link"
              >
                Vedi tutti i viaggi
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </section>
      )}

      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-gradient-to-br from-accent/5 to-primary/5" data-testid="instagram-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-3 mb-6 px-6 py-3 bg-white rounded-full shadow-lg">
              <Instagram size={28} className="text-accent" />
              <span className="font-semibold text-lg">40k+ follower</span>
            </div>
            <h2 className="font-heading font-light text-5xl md:text-7xl tracking-tight mb-6" data-testid="instagram-title">
              Seguici su Instagram
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-foreground/80 mb-10">
              Unisciti alla community di oltre 40.000 persone che condividono la passione per i
              viaggi autentici e lenti.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://instagram.com/chichi_inviaggio"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 rounded-full px-8 py-4 text-lg font-medium bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                data-testid="instagram-cta-button"
              >
                <Instagram size={24} />
                @chichi_inviaggio
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="https://www.tiktok.com/@chichi_inviaggio"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 rounded-full px-8 py-4 text-lg font-medium bg-foreground text-background hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                data-testid="tiktok-cta-button"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
                @chichi_inviaggio
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;